import React, { useState } from 'react';

const FolderTreeItem = ({ folder, level = 0, selectedFolder, onSelectFolder, allFolders }) => {
  // All folders closed by default
  const [isExpanded, setIsExpanded] = useState(false);
  
  const children = allFolders.filter(f => f.parent_id === folder.folder_id);
  const hasChildren = children.length > 0;
  
  const handleToggle = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };
  
  const handleSelect = () => {
    onSelectFolder(folder.name);
  };
  
  const isSelected = selectedFolder === folder.name;
  
  return (
    <div className="select-none">
      <div
        className={`flex items-center gap-3 py-2.5 px-3 rounded-lg cursor-pointer transition-all ${
          isSelected 
            ? 'bg-[#00D9FF]/10 text-white border-l-2 border-[#00D9FF]' 
            : 'hover:bg-[#141414] text-[#888] hover:text-white'
        }`}
        style={{ paddingLeft: `${level * 12 + 12}px` }}
        onClick={handleSelect}
      >
        {/* Expand/Collapse Arrow */}
        {hasChildren ? (
          <button
            onClick={handleToggle}
            className="w-4 h-4 flex items-center justify-center text-[#555] hover:text-white"
          >
            <svg className={`w-3 h-3 transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </button>
        ) : (
          <div className="w-4 h-4"></div>
        )}
        
        {/* Folder Icon */}
        <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#00D9FF]/20' : 'bg-[#00D9FF]/20'}`}>
          <svg className={`w-3.5 h-3.5 ${isSelected ? 'text-[#00D9FF]' : 'text-[#00D9FF]'}`} fill="currentColor" viewBox="0 0 24 24">
            <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
          </svg>
        </div>
        
        {/* Folder Name */}
        <span className="text-sm truncate flex-1 font-medium">{folder.name}</span>
        
        {/* Track Count */}
        {folder.track_count > 0 && (
          <span className="text-xs text-[#00D9FF] font-bold bg-[#00D9FF]/10 px-2 py-0.5 rounded">
            {folder.track_count}
          </span>
        )}
      </div>
      
      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {children.map(child => (
            <FolderTreeItem
              key={child.folder_id}
              folder={child}
              level={level + 1}
              selectedFolder={selectedFolder}
              onSelectFolder={onSelectFolder}
              allFolders={allFolders}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FolderTree = ({ folders, selectedFolder, onSelectFolder, totalTracks }) => {
  // Define the order for main folders
  const mainFolderOrder = [
    '🟢 1️⃣ NEW MUSIC',
    '🔥 2️⃣ LATIN SPOTLIGHT',
    '🎧 3️⃣ OPEN FORMAT',
    '🎉 4️⃣ EVENT READY',
    '🎚 5️⃣ DJ TOOLS',
    '🗂 6️⃣ ARCHIVE'
  ];
  
  const rootFolders = folders.filter(f => !f.parent_id || f.parent_id === 'root');
  
  // Sort root folders by the defined order, then alphabetically for others
  const sortedRootFolders = [...rootFolders].sort((a, b) => {
    const indexA = mainFolderOrder.indexOf(a.name);
    const indexB = mainFolderOrder.indexOf(b.name);
    
    // If both are main folders, sort by defined order
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // Main folders come first
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    // Others sorted alphabetically
    return a.name.localeCompare(b.name);
  });
  
  return (
    <div className="bg-[#0D0D0D] rounded-xl border border-[#1A1A1A] overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <h3 className="text-white font-bold text-sm flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          <div className="w-6 h-6 rounded bg-[#00D9FF]/20 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-[#00D9FF]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
            </svg>
          </div>
          Biblioteca
        </h3>
      </div>
      
      {/* All Tracks option */}
      <div className="p-2">
        <div
          className={`flex items-center gap-3 py-2.5 px-3 rounded-lg cursor-pointer transition-all ${
            selectedFolder === null 
              ? 'bg-[#00D9FF]/10 text-white border-l-2 border-[#00D9FF]' 
              : 'hover:bg-[#141414] text-[#888] hover:text-white'
          }`}
          onClick={() => onSelectFolder(null)}
        >
          <div className="w-4 h-4"></div>
          <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${selectedFolder === null ? 'bg-[#00D9FF]/20' : 'bg-[#00D9FF]/10'}`}>
            <svg className="w-3.5 h-3.5 text-[#00D9FF]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
            </svg>
          </div>
          <span className="text-sm font-medium flex-1">Todos los Tracks</span>
          <span className="text-xs text-[#00D9FF] font-bold bg-[#00D9FF]/10 px-2 py-0.5 rounded">{totalTracks}</span>
        </div>
        
        {/* Separator */}
        <div className="h-px bg-[#262626] my-2"></div>
        
        {/* Folder Tree */}
        <div className="max-h-[400px] overflow-y-auto">
          {sortedRootFolders.length > 0 ? (
            sortedRootFolders.map(folder => (
              <FolderTreeItem
                key={folder.folder_id}
                folder={folder}
                level={0}
                selectedFolder={selectedFolder}
                onSelectFolder={onSelectFolder}
                allFolders={folders}
              />
            ))
          ) : (
            folders.map(folder => (
              <FolderTreeItem
                key={folder.folder_id}
                folder={{...folder, parent_id: null}}
                level={0}
                selectedFolder={selectedFolder}
                onSelectFolder={onSelectFolder}
                allFolders={[]}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default FolderTree;
